// src/components/admin/AdminHeader.tsx - Refined & Clean Design
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  alpha,
  Tooltip,
  InputBase,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Notifications as NotificationsIcon,
  KeyboardArrowDown,
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  PersonAdd as PersonAddIcon,
  Article as ArticleIcon,
  FiberManualRecord as DotIcon,
  CheckCircle as CheckIcon,
  DeleteSweep as ClearAllIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import * as notificationApi from '../../services/notification';
import type { Notification } from '../../services/notification';

// Logo imports should be dynamic based on theme
import logoDark from '../../assets/logo-white.png';
import logoLight from '../../assets/logo-black.png';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? `linear-gradient(135deg, ${alpha('#0a0a0a', 0.95)} 0%, ${alpha('#1a1a1a', 0.9)} 100%)`
    : `linear-gradient(135deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f8f9fa', 0.9)} 100%)`,
  backdropFilter: 'blur(20px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.05)'
    : '0 4px 20px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.8)',
  borderBottom: theme.palette.mode === 'dark' 
    ? `1px solid ${alpha('#333', 0.3)}`
    : `1px solid ${alpha('#e0e0e0', 0.5)}`,
  transition: 'all 0.3s ease',
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 12,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.08)
    : alpha('#f5f5f5', 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.12)
      : alpha('#ffffff', 0.9),
  },
  '&:focus-within': {
    background: theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.15)
      : '#ffffff',
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  marginRight: theme.spacing(2),
  width: '100%',
  maxWidth: 300,
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(2),
    width: 'auto',
  },
  transition: 'all 0.3s ease'
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: alpha(theme.palette.text.primary, 0.6)
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.text.primary,
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.2, 1, 1.2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    fontSize: '0.9rem',
    '&::placeholder': {
      color: alpha(theme.palette.text.primary, 0.5),
      opacity: 1
    }
  },
}));

const CleanButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1),
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.05)
    : alpha('#f5f5f5', 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.1)
      : alpha('#ffffff', 0.8),
    transform: 'translateY(-1px)',
  }
}));

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  userType: string;
}

interface AdminHeaderProps {
  toggleTheme: () => void;
  toggleSidebar: () => void;
  currentTheme: string;
  isAuthenticated: boolean;
  user: User | null;
  onLogout: () => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({
  toggleTheme,
  toggleSidebar,
  currentTheme,
  user,
  onLogout
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);
  const isDarkMode = currentTheme === 'dark';

  const logo = isDarkMode ? logoDark : logoLight;

  // Fetch unread count on mount and every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const data = await notificationApi.getMyNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read
      if (!notification.isRead) {
        await notificationApi.markAsRead(notification.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
      }

      // Navigate if there's a link
      if (notification.link) {
        navigate(notification.link);
      }

      handleNotificationClose();
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      // Mark all as read (don't delete, they should remain in notifications page)
      await notificationApi.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      handleNotificationClose();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    if (type.includes('member') || type.includes('connection')) return <PersonAddIcon />;
    if (type.includes('article')) return <ArticleIcon />;
    return <NotificationsIcon />;
  };

  const getUserInitials = () => {
    if (user && user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    return 'AD';
  };

  return (
    <StyledAppBar position="fixed" elevation={0}>
      <Toolbar sx={{ minHeight: 72, px: { xs: 2, sm: 3 } }}>
        {/* Left Section */}
        <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
          <CleanButton
            color="inherit"
            aria-label="toggle sidebar"
            edge="start"
            onClick={toggleSidebar}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </CleanButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3, minWidth: 0 }}>
            <img 
              src={logo} 
              alt="Value Chain Global" 
              style={{
                height: 36,
                objectFit: 'contain'
              }}
            />
          </Box>

          <Chip
            icon={<AdminIcon />}
            label="Admin"
            size="small"
            sx={{
              background: isDarkMode 
                ? 'linear-gradient(135deg, #d9f316 0%, #b8d113 100%)'
                : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: isDarkMode ? '#000' : '#fff',
              fontWeight: 600,
              display: { xs: 'none', sm: 'flex' }
            }}
          />
        </Box>

        {/* Center Section */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', mx: 2 }}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search admin panel..."
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="System Status">
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                background: alpha(theme.palette.success.main, 0.1),
                mr: 1
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                }}
              />
              <Typography variant="caption" color="success.main" fontWeight={600}>
                Online
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}>
            <CleanButton onClick={toggleTheme}>
              {isDarkMode ? <LightIcon /> : <DarkIcon />}
            </CleanButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <CleanButton onClick={handleNotificationOpen}>
              <Badge
                badgeContent={unreadCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    minWidth: 16,
                    height: 16
                  }
                }}
              >
                <NotificationsIcon />
              </Badge>
            </CleanButton>
          </Tooltip>

          {/* User Menu */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: 1,
              px: 1,
              py: 0.5,
              borderRadius: 3,
              background: alpha(theme.palette.background.paper, 0.5),
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: alpha(theme.palette.background.paper, 0.8),
              }
            }}
            onClick={handleMenuOpen}
          >
            <Avatar 
              sx={{ 
                width: 32,
                height: 32,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 600,
                mr: { xs: 0, sm: 1 }
              }}
            >
              {getUserInitials()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                {user ? `${user.firstName} ${user.lastName}` : 'Admin'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Administrator
              </Typography>
            </Box>
            <KeyboardArrowDown 
              fontSize="small" 
              sx={{ 
                ml: 0.5,
                transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} 
            />
          </Box>
        </Box>
      </Toolbar>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.4)'
              : '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '& .MuiMenuItem-root': {
              borderRadius: 1,
              margin: theme.spacing(0.5),
              transition: 'background 0.2s ease'
            }
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        
        <MenuItem onClick={() => handleNavigate('/admin/dashboard')}>
          <DashboardIcon sx={{ mr: 2, fontSize: 20 }} />
          Dashboard
        </MenuItem>
        
        <MenuItem onClick={() => handleNavigate('/admin/profile')}>
          <AccountIcon sx={{ mr: 2, fontSize: 20 }} />
          Profile
        </MenuItem>
        
        <MenuItem onClick={() => handleNavigate('/admin/security')}>
          <SecurityIcon sx={{ mr: 2, fontSize: 20 }} />
          Security
        </MenuItem>
        
        <MenuItem onClick={() => handleNavigate('/admin/settings')}>
          <SettingsIcon sx={{ mr: 2, fontSize: 20 }} />
          Settings
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            mt: 1,
            width: 400,
            maxHeight: 500,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.4)'
              : '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        {/* Notification Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Notifications
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<CheckIcon />}
                  onClick={handleMarkAllAsRead}
                  sx={{ textTransform: 'none' }}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<ClearAllIcon />}
                  onClick={handleClearAll}
                  sx={{ textTransform: 'none' }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Notification List */}
        {loadingNotifications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.slice(0, 10).map((notification) => (
              <ListItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 2,
                  px: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                  background: notification.isRead ? 'transparent' : alpha(theme.palette.primary.main, 0.05),
                  cursor: 'pointer',
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: notification.isRead
                        ? alpha(theme.palette.text.secondary, 0.1)
                        : alpha(theme.palette.primary.main, 0.15),
                      color: notification.isRead ? 'text.secondary' : 'primary.main'
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={notification.isRead ? 400 : 600}>
                        {notification.title}
                      </Typography>
                      {!notification.isRead && (
                        <DotIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {notifications.length > 10 && (
          <Box sx={{ p: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, textAlign: 'center' }}>
            <Button
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={() => {
                handleNotificationClose();
                navigate('/admin/notifications');
              }}
            >
              View all notifications
            </Button>
          </Box>
        )}
      </Menu>
    </StyledAppBar>
  );
};

export default AdminHeader;